// Auth state for the /khilanet panel — persists the signed-in Kehilanet session (consent
// token + refresh token + cached profile) and bridges the short-lived 2FA token from the
// login screen to the OTP screen. The network calls live in khilanetApi.ts.

import type { KhilanetUserData } from './khilanetApi'

const AUTH_KEY = 'khilanet:auth.v2'
const PENDING_KEY = 'khilanet:auth.pending.v2'
// Storage key owned by Coupons.tsx (FILTERS_STORAGE_KEY). Cleared on sign-in so a fresh login
// always lands on the default 'general' tab — a single string match is cheaper than coupling.
const COUPON_FILTERS_KEY = 'mekomi.couponFilters.v1'

export interface KhilanetAuthState {
  /** The username the session was opened with (phone digits, or an email). Informational. */
  phone: string
  /** Kehilanet "consent" access token — also used as the panel's jwtToken. */
  accessToken: string
  refreshToken: string
  /** Epoch ms when accessToken expires; 0 if the upstream didn't report an expiry. */
  expiresAt: number
  signedInAt: number
  /** Profile + communities from the userData leg; absent if that call failed at sign-in. */
  user?: KhilanetUserData
}

// Transient state between Login -> OTP. sessionStorage: survives an OTP-page refresh but
// doesn't bleed across browser sessions.
export interface KhilanetPendingState {
  phone: string
  twoFAToken: string
  otpType: string
  requestedAt: number
}

export const normalizePhone = (raw: string): string => raw.replace(/\D/g, '')

const hasWindow = (): boolean => typeof window !== 'undefined'

// ---- pending (Login -> OTP bridge) -----------------------------------------------------------

export const setPending = (p: KhilanetPendingState): void => {
  if (!hasWindow()) return
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(p))
}

export const getPending = (): KhilanetPendingState | null => {
  if (!hasWindow()) return null
  const raw = window.sessionStorage.getItem(PENDING_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as KhilanetPendingState
  } catch {
    return null
  }
}

export const clearPending = (): void => {
  if (!hasWindow()) return
  window.sessionStorage.removeItem(PENDING_KEY)
}

// ---- signed-in session -----------------------------------------------------------------------

export const getAuth = (): KhilanetAuthState | null => {
  if (!hasWindow()) return null
  const raw = window.localStorage.getItem(AUTH_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as KhilanetAuthState
  } catch {
    return null
  }
}

/** Signed in if we hold an access token that hasn't expired (expiresAt 0 = treat as non-expiring). */
export const isAuthed = (): boolean => {
  const a = getAuth()
  if (!a || !a.accessToken) return false
  if (a.expiresAt && a.expiresAt <= Date.now()) return false
  return true
}

export const signIn = (state: Omit<KhilanetAuthState, 'signedInAt'>): void => {
  if (!hasWindow()) return
  const full: KhilanetAuthState = { ...state, signedInAt: Date.now() }
  window.localStorage.setItem(AUTH_KEY, JSON.stringify(full))
  window.localStorage.removeItem(COUPON_FILTERS_KEY)
  window.sessionStorage.removeItem(PENDING_KEY)
}

/** Replace just the token bits after a refresh, keeping the cached profile. */
export const updateTokens = (t: { accessToken: string; refreshToken: string; expiresAt: number }): void => {
  if (!hasWindow()) return
  const a = getAuth()
  if (!a) return
  window.localStorage.setItem(AUTH_KEY, JSON.stringify({ ...a, ...t }))
}

export const signOut = (): void => {
  if (!hasWindow()) return
  window.localStorage.removeItem(AUTH_KEY)
  window.sessionStorage.removeItem(PENDING_KEY)
}

export const getAccessToken = (): string | null => getAuth()?.accessToken ?? null
